import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useManagedPositions } from "@/Common/hooks/Positions";
import { useCommissionCyclesData } from "@/Washouts/hooks/common/useCommissionCyclesData";
import useConfigurationData from "@Common/hooks/Configuration/useConfigurationData";
import { useSystemSettingSubscription } from "@/Settings/hooks/useSystemSettingsSubscription";

import PayPlansMenuConfiguration from "@/Layouts/views/Header/constants/PayPlansMenuConfiguration";
import washoutsMenuConfiguration from "@/Layouts/views/Header/constants/WashoutsMenuConfiguration";
import motivatorsMenuConfiguration from "@/Layouts/views/Header/constants/MotivatorsMenuConfiguration";

// Types
import type { Action } from "@Constants/actionsList";
import type {
	HeaderMenuConfiguration,
	HeaderMenuFooterItemConfiguration,
	HeaderMenuItemConfiguration,
	HeaderNavbarProps,
	WithAccess,
} from "@/Layouts/views/Header/interfaces";
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { ISystemSettings } from "@/Settings/types/Common/model";
import { getAdministrationToolsOpportunityMining } from "@/Motivators/api/autoAlerts";

const useNavbarConfig = () => {
	const { useAuth: isAuthorized } = useAuthorization();
	const queryClient = useQueryClient();

	const { isLoading: enableReviewWashoutLoading, locationId, commissionCycle, partnerPayPeriod, payPeriod } = useCommissionCyclesData();
	useSystemSettingSubscription(
		locationId,
		(response: GraphqlQueryResponse<"onUpdateSystemSettings", ISystemSettings>["data"]) => {
			if (response.data?.onUpdateSystemSettings.drawModalities) {
				queryClient.invalidateQueries({ queryKey: ["commissionCycles", locationId] });
			}
		},
		"header"
	);

	const { configuration } = useConfigurationData({ locationId });

	const enableReviewWashout = useMemo(() => {
		const { status } = commissionCycle ?? {};
		const finalPositionStatus = partnerPayPeriod
			? [...partnerPayPeriod.positionStatus, ...(payPeriod?.positionStatus ?? [])]
			: (payPeriod?.positionStatus ?? []);
		return (
			status !== "FINALIZED" &&
			payPeriod?.status !== "COMPLETED" &&
			finalPositionStatus.some((positionStatus) => !["UNREVIEWABLE", "PENDING"].includes(positionStatus.status))
		);
	}, [commissionCycle]);

	const { isPositionManaged, matchPosition } = useManagedPositions({
		queryOpts: {
			staleTime: Infinity,
		},
	});

	const hasAccessGranted = useCallback(
		(action: Action | [Action], position: string) => {
			const isManaged = matchPosition(position)?.length ?? isPositionManaged(position);
			return isManaged && isAuthorized(action);
		},
		[isAuthorized, matchPosition, isPositionManaged]
	);

	const { data: opportunityMiningDescription } = useQuery({
		queryKey: ["getProviderNameOpportunityMining", locationId],
		queryFn: async () => {
			const response = await getAdministrationToolsOpportunityMining(locationId);
			if (response?.integrations?.opportunityMining?.provider) {
				const firstProvider = response.integrations.opportunityMining.provider.shift();
				return firstProvider?.providerName || "No Provider";
			}
			return "No Provider";
		},
		staleTime: Infinity,
		enabled: Boolean(locationId),
	});
	const checkItemAccess: <T extends HeaderMenuItemConfiguration | HeaderMenuFooterItemConfiguration>(item: T) => WithAccess<T> =
		useCallback(
			(item) => {
				const { accessConfiguration } = item;
				if (typeof accessConfiguration === "boolean") {
					return { ...item, hasAccess: accessConfiguration };
				}

				let hasExtraAuthorization = true;
				let isLoading = false;
				for (const authorization of accessConfiguration.extraAuthorization ?? []) {
					if (authorization === "enableReviewWashout" && !enableReviewWashout) {
						hasExtraAuthorization = false;
						isLoading = enableReviewWashoutLoading;
					}
				}

				let hasAccess = false;
				if (accessConfiguration.type === "authorized") {
					hasAccess = isAuthorized(accessConfiguration.access);
				}
				if (accessConfiguration.type === "granted") {
					hasAccess = hasAccessGranted(accessConfiguration.access, accessConfiguration.position);
				}
				if (item.label === "Opportunity Mining") {
					(item as HeaderMenuItemConfiguration).description = opportunityMiningDescription;
				}
				return { ...item, hasAccess: hasAccess && hasExtraAuthorization, loading: isLoading };
			},
			[enableReviewWashout, enableReviewWashoutLoading, hasAccessGranted, isAuthorized, opportunityMiningDescription]
		);

	const buildMenuProps = useCallback(
		(navbarMenuConfig: HeaderMenuConfiguration): HeaderNavbarProps => {
			const { menuConfiguration } = navbarMenuConfig;

			return {
				title: navbarMenuConfig.title,
				actions: navbarMenuConfig.actions,
				menuConfiguration: {
					...menuConfiguration,
					sections: menuConfiguration.sections.map((section) => {
						return {
							name: section.name,
							menuItemGroups: section.menuItemGroups.map((group) => {
								return {
									id: group.id,
									menuItems: group.menuItems.map((menuItem) => {
										return {
											...checkItemAccess(menuItem),
										};
									}),
								};
							}),
						};
					}),
					footerItems: menuConfiguration.footerItems?.map((item) => {
						return checkItemAccess(item);
					}),
				},
			};
		},
		[checkItemAccess, configuration]
	);

	const navbarConfig: HeaderNavbarProps[] = useMemo(
		() => [PayPlansMenuConfiguration, washoutsMenuConfiguration, motivatorsMenuConfiguration].map(buildMenuProps),
		[buildMenuProps, configuration]
	);

	return { navbarConfig };
};

export default useNavbarConfig;
